import type {
  SupportV2MigrationFailureSample,
  SupportV2MigrationProgress,
  SupportV2MigrationRunProgress,
  SupportV2MigrationStepProgress,
} from "@redotech/admin-sdk/rpc/schema/support/get-support-v2-migration-progress";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import CheckIcon from "@redotech/redo-icons/check.svg";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import StopIcon from "@redotech/redo-icons/stop-circle.svg";
import {
  RedoBadge,
  type RedoBadgeProps,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { RedoProgressBar } from "@redotech/redo-web/arbiter-components/progress-bar/redo-progress-bar";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, type JSXElementConstructor } from "react";
import {
  formatCount,
  formatDuration,
  formatProgressDate,
  formatRunStepPlan,
  formatStepName,
  getProcessedCount,
  getStepPercentage,
  isActiveRunStatus,
  parseRunError,
} from "./utils";

interface BadgeMeta {
  color: RedoBadgeProps["color"];
  Icon?: JSXElementConstructor<unknown>;
  text: string;
}

const runStatusMeta: Record<
  SupportV2MigrationRunProgress["status"],
  BadgeMeta
> = {
  cancelled: { color: "gray", Icon: StopIcon, text: "Cancelled" },
  completed: { color: "success", Icon: CheckIcon, text: "Completed" },
  completed_with_failures: {
    color: "warning",
    Icon: AlertTriangleIcon,
    text: "Completed with failures",
  },
  failed: { color: "error", Icon: AlertTriangleIcon, text: "Failed" },
  pending: { color: "gray", Icon: ClockIcon, text: "Pending" },
  running: { color: "blue", Icon: ClockIcon, text: "Running" },
};

const stepStatusMeta: Record<
  SupportV2MigrationStepProgress["status"],
  BadgeMeta
> = {
  completed: { color: "success", Icon: CheckIcon, text: "Complete" },
  completed_with_failures: {
    color: "warning",
    Icon: AlertTriangleIcon,
    text: "Completed with failures",
  },
  failed: { color: "error", Icon: AlertTriangleIcon, text: "Failed" },
  pending: { color: "gray", Icon: ClockIcon, text: "Pending" },
  running: { color: "blue", Icon: ClockIcon, text: "Running" },
  skipped: { color: "gray", text: "Skipped" },
};

export const EmptyProgressState = memo(function EmptyProgressState() {
  return (
    <Flex
      bgColor="secondary"
      borderColor="secondary"
      borderRadius="md"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="xs"
      p="lg"
    >
      <Text fontSize="sm" fontWeight="medium">
        No migration progress recorded yet.
      </Text>
      <Text fontSize="xs" textColor="secondary">
        Start a migration to create a durable run, then this card will show live
        status, step counts, failures, and history.
      </Text>
    </Flex>
  );
});

export const FailedRunResumeNotice = memo(function FailedRunResumeNotice({
  run,
}: {
  run: SupportV2MigrationRunProgress;
}) {
  return (
    <Flex
      borderColor="error"
      borderRadius="md"
      borderStyle="solid"
      borderWidth="1px"
      gap="md"
      justify="space-between"
      p="md"
      wrap="wrap"
    >
      <Flex dir="column" gap="xs" grow={1}>
        <Flex align="center" gap="xs">
          <AlertTriangleIcon />
          <Text fontSize="sm" fontWeight="medium" textColor="error">
            Failed run can be resumed
          </Text>
        </Flex>
        <Text fontSize="xs" textColor="secondary">
          Use the primary run control to continue the existing workflow from
          durable checkpoints.
        </Text>
        <Text fontSize="xs" textColor="secondary">
          Plan: {formatRunStepPlan(run)}
        </Text>
      </Flex>
    </Flex>
  );
});

export const LatestRunOverview = memo(function LatestRunOverview({
  cancelPending,
  onCancelRun,
  overallPercentage,
  progress,
}: {
  cancelPending: boolean;
  onCancelRun(workflowId: string): void;
  overallPercentage: number;
  progress: SupportV2MigrationProgress;
}) {
  const run = progress.run;
  if (!run) {
    return null;
  }

  const processedCount = progress.steps.reduce(
    (sum, step) => sum + getProcessedCount(step),
    0,
  );
  const totalCount = progress.steps.reduce(
    (sum, step) => sum + step.totalCount,
    0,
  );
  const failedCount = progress.steps.reduce(
    (sum, step) => sum + step.failedCount,
    0,
  );
  const warningCount = progress.steps.reduce(
    (sum, step) => sum + step.warningCount,
    0,
  );
  const currentStep = progress.currentStepPhase
    ? formatStepName(progress.currentStepPhase)
    : "None";

  return (
    <Flex
      borderColor="secondary"
      borderRadius="md"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="md"
      p="lg"
    >
      <Flex align="center" gap="md" justify="space-between" wrap="wrap">
        <Flex dir="column" gap="xxs">
          <Text fontSize="sm" fontWeight="medium">
            Latest run
          </Text>
          <Text fontSize="xs" textColor="secondary">
            Updated {formatProgressDate(run.updatedAt)}
          </Text>
        </Flex>
        <Flex align="center" gap="xs" wrap="wrap">
          <RunStatusBadge status={run.status} />
          {isActiveRunStatus(run.status) && (
            <RedoButton
              hierarchy="secondary"
              IconLeading={StopIcon}
              onClick={() => onCancelRun(run.workflowId)}
              pending={cancelPending}
              size="sm"
              text="Cancel run"
              theme="destructive"
            />
          )}
        </Flex>
      </Flex>

      <Flex dir="column" gap="xs">
        <Flex align="center" gap="md" justify="space-between">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Overall progress
          </Text>
          <Text fontSize="xs" textColor="secondary">
            {overallPercentage}%
          </Text>
        </Flex>
        <RedoProgressBar
          label
          labelOrientation="right"
          labelStyle="none"
          percentage={overallPercentage}
          style="thin"
        />
      </Flex>

      <Flex gap="sm" wrap="wrap">
        <ProgressStat label="Current step" value={currentStep} />
        <ProgressStat
          label="Processed"
          value={
            totalCount > 0
              ? `${formatCount(processedCount)} / ${formatCount(totalCount)}`
              : formatCount(processedCount)
          }
        />
        <ProgressStat
          label="Duration"
          value={formatDuration(run.startedAt, run.completedAt)}
        />
        <ProgressStat
          label="Failures"
          tone="error"
          value={formatCount(failedCount)}
        />
        <ProgressStat
          label="Warnings"
          tone="warning"
          value={formatCount(warningCount)}
        />
      </Flex>

      {run.lastError && <RunErrorCallout error={run.lastError} />}
    </Flex>
  );
});

const RunErrorCallout = memo(function RunErrorCallout({
  error,
}: {
  error: string;
}) {
  const details = useMemo(() => parseRunError(error), [error]);

  return (
    <Flex
      borderColor="error"
      borderRadius="md"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="sm"
      p="md"
    >
      <Flex align="center" gap="xs">
        <AlertTriangleIcon />
        <Text fontSize="sm" fontWeight="medium" textColor="error">
          {details.title}
        </Text>
      </Flex>
      <Text fontSize="xs" textColor="secondary">
        {details.description}
      </Text>
      {details.buckets.length > 0 && (
        <Flex gap="xs" wrap="wrap">
          {details.buckets.map((bucket) => (
            <RedoBadge
              color="warning"
              key={bucket.label}
              text={`${bucket.label}: ${formatCount(bucket.count)}`}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
});

const ProgressStat = memo(function ProgressStat({
  label,
  tone = "neutral",
  value,
}: {
  label: string;
  tone?: "neutral" | "warning" | "error";
  value: string;
}) {
  const textColor: "error" | "primary" | "warning" =
    tone === "error" ? "error" : tone === "warning" ? "warning" : "primary";

  return (
    <Flex
      bgColor="secondary"
      borderColor="secondary"
      borderRadius="md"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="xxs"
      grow={1}
      p="md"
    >
      <Text fontSize="xxs" fontWeight="medium" textColor="secondary">
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="medium" textColor={textColor}>
        {value}
      </Text>
    </Flex>
  );
});

export const StepProgressList = memo(function StepProgressList({
  steps,
}: {
  steps: SupportV2MigrationStepProgress[];
}) {
  if (steps.length === 0) {
    return (
      <Flex dir="column" gap="xs">
        <Text fontSize="sm" fontWeight="medium">
          Steps
        </Text>
        <Text fontSize="xs" textColor="secondary">
          Step rows will appear once the workflow starts executing.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="sm">
      <Text fontSize="sm" fontWeight="medium">
        Step progress
      </Text>
      {steps.map((step) => (
        <StepProgressRow key={step.id} step={step} />
      ))}
    </Flex>
  );
});

const StepProgressRow = memo(function StepProgressRow({
  step,
}: {
  step: SupportV2MigrationStepProgress;
}) {
  const percentage = getStepPercentage(step);

  return (
    <Flex
      borderColor="secondary"
      borderRadius="md"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="sm"
      p="md"
    >
      <Flex align="center" gap="md" justify="space-between" wrap="wrap">
        <Flex dir="column" gap="xxs" grow={1}>
          <Text fontSize="sm" fontWeight="medium">
            {formatStepName(step.phase)}
          </Text>
          <Text fontSize="xs" textColor="secondary">
            Attempt {step.attempt} - Updated{" "}
            {formatProgressDate(step.updatedAt)}
          </Text>
        </Flex>
        <StepStatusBadge status={step.status} />
      </Flex>

      {step.totalCount > 0 && (
        <RedoProgressBar
          label
          labelOrientation="right"
          labelStyle="text"
          percentage={percentage}
          style="thin"
        />
      )}

      <Flex gap="xs" wrap="wrap">
        <RedoBadge
          color="gray"
          text={`Total ${formatCount(step.totalCount)}`}
        />
        <RedoBadge
          color="success"
          text={`Inserted ${formatCount(step.insertedCount)}`}
        />
        <RedoBadge
          color="blue"
          text={`Updated ${formatCount(step.updatedCount)}`}
        />
        <RedoBadge
          color="gray"
          text={`Skipped ${formatCount(step.skippedCount)}`}
        />
        <RedoBadge
          color="error"
          text={`Failed ${formatCount(step.failedCount)}`}
        />
        <RedoBadge
          color="warning"
          text={`Warnings ${formatCount(step.warningCount)}`}
        />
      </Flex>

      {step.lastError && (
        <Text fontSize="xs" textColor="error">
          Last error: {step.lastError}
        </Text>
      )}
    </Flex>
  );
});

export const FailureSamples = memo(function FailureSamples({
  failures,
}: {
  failures: SupportV2MigrationFailureSample[];
}) {
  if (failures.length === 0) {
    return null;
  }

  return (
    <RedoCollapseSubsection
      defaultCollapsed
      title={`Failure samples (${failures.length})`}
    >
      <Flex dir="column" gap="sm" pl="lg">
        {failures.map((failure) => (
          <Flex
            borderColor="error"
            borderRadius="md"
            borderStyle="solid"
            borderWidth="1px"
            dir="column"
            gap="xs"
            key={failure.id}
            p="md"
          >
            <Flex align="center" gap="xs" wrap="wrap">
              <RedoBadge color="error" text={formatStepName(failure.phase)} />
              <RedoBadge color="gray" text={failure.entity} />
              <RedoBadge color="gray" monospace text={failure.legacyId} />
              <RedoBadge
                color="warning"
                text={`${formatCount(failure.occurrenceCount)}x`}
              />
            </Flex>
            <Text fontSize="xs" textColor="error">
              {failure.error}
            </Text>
            <Text fontSize="xxs" textColor="secondary">
              First seen {formatProgressDate(failure.firstSeenAt)} - Last seen{" "}
              {formatProgressDate(failure.lastSeenAt)}
            </Text>
          </Flex>
        ))}
      </Flex>
    </RedoCollapseSubsection>
  );
});

export const RunHistory = memo(function RunHistory({
  history,
  latestRunId,
}: {
  history: SupportV2MigrationRunProgress[];
  latestRunId: string;
}) {
  if (history.length === 0) {
    return null;
  }

  return (
    <RedoCollapseSubsection
      defaultCollapsed={history.length <= 1}
      title={`Migration history (${history.length})`}
    >
      <Flex dir="column" gap="xs" pl="lg">
        {history.map((run) => (
          <RunHistoryRow
            isLatest={run.id === latestRunId}
            key={run.id}
            run={run}
          />
        ))}
      </Flex>
    </RedoCollapseSubsection>
  );
});

const RunHistoryRow = memo(function RunHistoryRow({
  isLatest,
  run,
}: {
  isLatest: boolean;
  run: SupportV2MigrationRunProgress;
}) {
  return (
    <Flex
      align="center"
      borderColor="secondary"
      borderRadius="md"
      borderStyle="solid"
      borderWidth="1px"
      gap="md"
      justify="space-between"
      p="md"
      wrap="wrap"
    >
      <Flex dir="column" gap="xxs" grow={1}>
        <Flex align="center" gap="xs" wrap="wrap">
          {isLatest && <RedoBadge color="blue" size="sm" text="Latest" />}
          <Text
            fontFamily="monospace"
            fontSize="xs"
            overflow="hidden"
            textColor="secondary"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {run.workflowId}
          </Text>
        </Flex>
        <Text fontSize="xxs" textColor="secondary">
          Started {formatProgressDate(run.startedAt)} - Updated{" "}
          {formatProgressDate(run.updatedAt)}
        </Text>
        <Text fontSize="xxs" textColor="secondary">
          Steps: {run.requestedSteps.map(formatStepName).join(", ")}
        </Text>
      </Flex>
      <RunStatusBadge status={run.status} />
    </Flex>
  );
});

export const RunStatusBadge = memo(function RunStatusBadge({
  status,
}: {
  status: SupportV2MigrationRunProgress["status"];
}) {
  return <StatusBadge meta={runStatusMeta[status]} />;
});

const StepStatusBadge = memo(function StepStatusBadge({
  status,
}: {
  status: SupportV2MigrationStepProgress["status"];
}) {
  return <StatusBadge meta={stepStatusMeta[status]} />;
});

const StatusBadge = memo(function StatusBadge({ meta }: { meta: BadgeMeta }) {
  return (
    <RedoBadge
      color={meta.color}
      segmentLeading={
        meta.Icon ? { type: "icon", Icon: meta.Icon, size: "sm" } : undefined
      }
      size="sm"
      text={meta.text}
    />
  );
});
